import { useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, Download } from "lucide-react";
import { API, useDashboard, useRefresh } from "../lib/api";
import { Loading, PageHeader } from "../components/PageHeader";
import { ReportRow } from "../components/ReportRow";
import { Chips, SearchBox } from "../components/Filters";

const HEAD = ["Country", "Site", "Item", "Name", "2 wk max", "3 wk max", "4 wk max", "Stock / total inv.", "Source", "DOH", "Weeks cover", "Value", "Status", "Reason for low DOH", "Next replenishment", "Replen. qty", "Comments"];

export default function WeeklyReport() {
  const { data, isLoading } = useDashboard();
  const refresh = useRefresh();
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("ALL");
  const [status, setStatus] = useState("ALL");
  const countries = useMemo(() => ["ALL", ...new Set((data?.rows || []).map((r) => r.country))], [data]);
  if (isLoading || !data) return <Loading />;
  const rows = data.rows.filter((r) => {
    if (q && !`${r.item} ${r.site} ${r.friendly_name}`.toLowerCase().includes(q.toLowerCase())) return false;
    if (country !== "ALL" && r.country !== country) return false;
    if (status === "LOW" && r.status !== "low") return false;
    if (status === "OK" && r.status !== "ok") return false;
    if (status === "INCOMPLETE" && !(r.status === "low" && !(r.reason_for_low_doh && r.next_replenishment_date && r.replenishment_quantity !== null))) return false;
    return true;
  });
  return (
    <div data-testid="report-page">
      <PageHeader kicker={`${data.week_label || "No forecast"} · target ${data.kpis.target_doh} days`} title="Amazon Weekly Report">
        <a href={`${API}/export`} data-testid="export-excel-btn" className="flex items-center gap-2 rounded-md bg-sky-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-sky-500">
          <Download className="h-4 w-4" /> Download Amazon export
        </a>
      </PageHeader>
      <p className="rise mb-6 max-w-3xl text-sm leading-relaxed text-slate-400">
        Each product's stock is shared across its Amazon FC sites in proportion to their forecast. Lines below target need a reason code,
        next replenishment date and quantity before you submit to Supplier Connect.
        {!data.settings.month_end && " Monthly forecast and capacity columns export as 0 until you switch on month-end week in Settings."}
      </p>
      {data.kpis.low_rows_incomplete > 0 ? (
        <div data-testid="export-readiness-warning" className="rise mb-6 flex items-center gap-2 rounded-md border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300">
          <AlertTriangle className="h-4 w-4" />
          {data.kpis.low_rows_incomplete} low DOH line(s) still need a reason, next replenishment date and quantity — Amazon's checker will reject the file without them.
          <button data-testid="show-incomplete-btn" onClick={() => setStatus("INCOMPLETE")} className="ml-auto underline hover:text-red-200">Show them</button>
        </div>
      ) : (
        <div data-testid="export-ready" className="rise mb-6 flex items-center gap-2 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-300">
          <CheckCircle2 className="h-4 w-4" /> All low DOH lines are complete — the export is ready for Supplier Connect.
        </div>
      )}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SearchBox value={q} onChange={setQ} testId="report-search" placeholder="Search item, site…" />
        <Chips options={countries} value={country} onChange={setCountry} testPrefix="report-country" />
        <Chips options={["ALL", "LOW", "OK", "INCOMPLETE"]} value={status} onChange={setStatus} testPrefix="report-status-filter" />
        <span data-testid="report-count" className="num ml-auto text-xs text-slate-500">{rows.length} of {data.rows.length} lines</span>
      </div>
      <div className="max-h-[70vh] overflow-auto rounded-lg border border-slate-800 bg-slate-900/60 shadow-xl">
        <table className="data-table w-full text-sm" data-testid="report-table">
          <thead><tr>{HEAD.map((h) => <th key={h}>{h}</th>)}</tr></thead>
          <tbody>{rows.map((r) => <ReportRow key={r.key} r={r} reasons={data.reason_codes} onSaved={refresh} />)}</tbody>
        </table>
        {rows.length === 0 && <div data-testid="report-empty" className="p-8 text-sm text-slate-500">No lines match these filters.</div>}
      </div>
    </div>
  );
}
